import React, { useMemo, useState } from "react";
import { ImageUploadField } from "../../shared/ImageUploadField";
import { blogInputClass } from "./BlogEditorFormFields";
import {
  SECTION_TYPES,
  createEmptyPoint,
  createEmptySection,
  parseDetailSections,
  serializeDetailSections
} from "./detailSectionsSchema";
import { TableGridEditor } from "./TableGridEditor";

const fieldLabelClass = "mb-1.5 block text-xs font-medium text-slate-600";
const cardClass = "rounded-xl border border-slate-200 bg-slate-50/70 p-4";

function PointListEditor({ points, onChange }) {
  const list = Array.isArray(points) ? points : [];

  return (
    <div className="space-y-3">
      {list.map((point, index) => (
        <div key={`point-${index}`} className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-xs font-semibold text-slate-500">Point {index + 1}</span>
            <button
              type="button"
              className="text-xs font-medium text-red-600 hover:underline"
              onClick={() => onChange(list.filter((_, i) => i !== index))}
            >
              Remove
            </button>
          </div>
          <input
            className={`${blogInputClass} mb-2`}
            placeholder="Title"
            value={point.title || ""}
            onChange={(e) => {
              const next = list.map((item, i) =>
                i === index ? { ...item, title: e.target.value } : item
              );
              onChange(next);
            }}
          />
          <textarea
            className={blogInputClass}
            rows={2}
            placeholder="Short description"
            value={point.text || ""}
            onChange={(e) => {
              const next = list.map((item, i) =>
                i === index ? { ...item, text: e.target.value } : item
              );
              onChange(next);
            }}
          />
        </div>
      ))}
      <button
        type="button"
        className="rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-white"
        onClick={() => onChange([...list, createEmptyPoint()])}
      >
        + Add point
      </button>
    </div>
  );
}

function StringListEditor({ items, onChange, placeholder = "Item" }) {
  const list = Array.isArray(items) ? items : [];

  return (
    <div className="space-y-2">
      {list.map((item, index) => (
        <div key={`item-${index}`} className="flex gap-2">
          <input
            className={blogInputClass}
            placeholder={`${placeholder} ${index + 1}`}
            value={item || ""}
            onChange={(e) => {
              const next = list.map((v, i) => (i === index ? e.target.value : v));
              onChange(next);
            }}
          />
          <button
            type="button"
            className="shrink-0 rounded-lg border border-slate-200 px-2 text-xs text-red-600 hover:bg-white"
            onClick={() => onChange(list.filter((_, i) => i !== index))}
          >
            ✕
          </button>
        </div>
      ))}
      <button
        type="button"
        className="rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-white"
        onClick={() => onChange([...list, ""])}
      >
        + Add item
      </button>
    </div>
  );
}

function SectionFields({ section, onChange }) {
  const patch = (partial) => onChange({ ...section, ...partial });

  if (section.type === "hero") {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className={fieldLabelClass}>Category tag</label>
            <input
              className={blogInputClass}
              value={section.category || ""}
              onChange={(e) => patch({ category: e.target.value })}
              placeholder="HONEY"
            />
          </div>
          <div>
            <label className={fieldLabelClass}>Image overlay tag</label>
            <input
              className={blogInputClass}
              value={section.imageTag || ""}
              onChange={(e) => patch({ imageTag: e.target.value })}
              placeholder="HONEY"
            />
          </div>
        </div>
        <div>
          <label className={fieldLabelClass}>Title override (optional — site can use product name)</label>
          <input
            className={blogInputClass}
            value={section.title || ""}
            onChange={(e) => patch({ title: e.target.value })}
          />
        </div>
        <div>
          <label className={fieldLabelClass}>Description</label>
          <textarea
            className={blogInputClass}
            rows={3}
            value={section.description || ""}
            onChange={(e) => patch({ description: e.target.value })}
          />
        </div>
        <div>
          <label className={fieldLabelClass}>Rating / caption under image</label>
          <input
            className={blogInputClass}
            value={section.ratingText || ""}
            onChange={(e) => patch({ ratingText: e.target.value })}
          />
        </div>
        <div>
          <label className={fieldLabelClass}>Feature points</label>
          <PointListEditor
            points={section.features}
            onChange={(features) => patch({ features })}
          />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className={fieldLabelClass}>Primary CTA label</label>
            <input
              className={blogInputClass}
              value={section.primaryCta?.label || ""}
              onChange={(e) =>
                patch({ primaryCta: { ...(section.primaryCta || {}), label: e.target.value } })
              }
            />
            <label className={`${fieldLabelClass} mt-2`}>Primary CTA URL</label>
            <input
              className={blogInputClass}
              value={section.primaryCta?.url || ""}
              onChange={(e) =>
                patch({ primaryCta: { ...(section.primaryCta || {}), url: e.target.value } })
              }
            />
          </div>
          <div>
            <label className={fieldLabelClass}>Secondary CTA label</label>
            <input
              className={blogInputClass}
              value={section.secondaryCta?.label || ""}
              onChange={(e) =>
                patch({
                  secondaryCta: { ...(section.secondaryCta || {}), label: e.target.value }
                })
              }
            />
            <label className={`${fieldLabelClass} mt-2`}>Secondary CTA URL</label>
            <input
              className={blogInputClass}
              value={section.secondaryCta?.url || ""}
              onChange={(e) =>
                patch({
                  secondaryCta: { ...(section.secondaryCta || {}), url: e.target.value }
                })
              }
            />
          </div>
        </div>
      </div>
    );
  }

  if (section.type === "useCases") {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className={fieldLabelClass}>Badge</label>
            <input
              className={blogInputClass}
              value={section.badge || ""}
              onChange={(e) => patch({ badge: e.target.value })}
            />
          </div>
          <div>
            <label className={fieldLabelClass}>Title</label>
            <input
              className={blogInputClass}
              value={section.title || ""}
              onChange={(e) => patch({ title: e.target.value })}
            />
          </div>
        </div>
        <div>
          <label className={fieldLabelClass}>Description</label>
          <textarea
            className={blogInputClass}
            rows={3}
            value={section.description || ""}
            onChange={(e) => patch({ description: e.target.value })}
          />
        </div>
        <ImageUploadField
          label="Section image"
          value={section.image || ""}
          onChange={(image) => patch({ image })}
          labelClassName={fieldLabelClass}
        />
        <div>
          <label className={fieldLabelClass}>Use case pills</label>
          <StringListEditor
            items={section.items}
            onChange={(items) => patch({ items })}
            placeholder="Use case"
          />
        </div>
      </div>
    );
  }

  if (section.type === "featureSplit") {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className={fieldLabelClass}>Badge</label>
            <input
              className={blogInputClass}
              value={section.badge || ""}
              onChange={(e) => patch({ badge: e.target.value })}
            />
          </div>
          <div>
            <label className={fieldLabelClass}>Image position</label>
            <select
              className={blogInputClass}
              value={section.imagePosition || "left"}
              onChange={(e) => patch({ imagePosition: e.target.value })}
            >
              <option value="left">Image left</option>
              <option value="right">Image right</option>
            </select>
          </div>
        </div>
        <div>
          <label className={fieldLabelClass}>Section title</label>
          <input
            className={blogInputClass}
            value={section.title || ""}
            onChange={(e) => patch({ title: e.target.value })}
          />
        </div>
        <div>
          <label className={fieldLabelClass}>Intro text</label>
          <textarea
            className={blogInputClass}
            rows={3}
            value={section.description || ""}
            onChange={(e) => patch({ description: e.target.value })}
          />
        </div>
        <ImageUploadField
          label="Section image"
          value={section.image || ""}
          onChange={(image) => patch({ image })}
          labelClassName={fieldLabelClass}
        />
        <div>
          <label className={fieldLabelClass}>Card title</label>
          <input
            className={blogInputClass}
            value={section.cardTitle || ""}
            onChange={(e) => patch({ cardTitle: e.target.value })}
          />
        </div>
        <div>
          <label className={fieldLabelClass}>Card points</label>
          <PointListEditor points={section.points} onChange={(points) => patch({ points })} />
        </div>
      </div>
    );
  }

  if (section.type === "featureList") {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className={fieldLabelClass}>Title</label>
            <input
              className={blogInputClass}
              value={section.title || ""}
              onChange={(e) => patch({ title: e.target.value })}
            />
          </div>
          <div>
            <label className={fieldLabelClass}>Image position</label>
            <select
              className={blogInputClass}
              value={section.imagePosition || "right"}
              onChange={(e) => patch({ imagePosition: e.target.value })}
            >
              <option value="right">Image right</option>
              <option value="left">Image left</option>
            </select>
          </div>
        </div>
        <ImageUploadField
          label="Section image"
          value={section.image || ""}
          onChange={(image) => patch({ image })}
          labelClassName={fieldLabelClass}
        />
        <div>
          <label className={fieldLabelClass}>Feature points</label>
          <PointListEditor points={section.points} onChange={(points) => patch({ points })} />
        </div>
      </div>
    );
  }

  if (section.type === "infographics") {
    const images = Array.isArray(section.images) ? section.images : [];
    return (
      <div className="space-y-3">
        <p className="text-xs text-slate-500">
          Upload designed infographic images (recommended for complex layouts).
        </p>
        {images.map((image, index) => (
          <div key={`infographic-${index}`} className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500">Infographic {index + 1}</span>
              <button
                type="button"
                className="text-xs font-medium text-red-600 hover:underline"
                onClick={() => patch({ images: images.filter((_, i) => i !== index) })}
              >
                Remove
              </button>
            </div>
            <ImageUploadField
              label="Image"
              value={image || ""}
              onChange={(next) => {
                const updated = images.map((v, i) => (i === index ? next : v));
                patch({ images: updated });
              }}
              labelClassName={fieldLabelClass}
            />
          </div>
        ))}
        <button
          type="button"
          className="rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-white"
          onClick={() => patch({ images: [...images, ""] })}
        >
          + Add infographic image
        </button>
      </div>
    );
  }

  if (section.type === "table") {
    return (
      <TableGridEditor
        value={{
          caption: section.caption || "",
          headers: section.headers,
          rows: section.rows
        }}
        onChange={(table) =>
          patch({
            caption: table.caption,
            headers: table.headers,
            rows: table.rows
          })
        }
      />
    );
  }

  return <p className="text-sm text-slate-500">Unknown section type.</p>;
}

/**
 * Form-based editor for structured product/service detail sections.
 * Stores a JSON document in `form.sectionsJson`.
 */
export function DetailSectionsEditor({ form, setForm }) {
  const [addType, setAddType] = useState("hero");
  const [showJson, setShowJson] = useState(false);
  const [jsonError, setJsonError] = useState("");

  const doc = useMemo(() => parseDetailSections(form.sectionsJson), [form.sectionsJson]);
  const sections = doc.sections || [];

  const commit = (nextDoc) => {
    setJsonError("");
    setForm((f) => ({
      ...f,
      sectionsJson: serializeDetailSections(nextDoc) || ""
    }));
  };

  const updateSection = (index, nextSection) => {
    const next = sections.map((s, i) => (i === index ? nextSection : s));
    commit({ ...doc, sections: next });
  };

  const moveSection = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= sections.length) return;
    const next = [...sections];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    commit({ ...doc, sections: next });
  };

  return (
    <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Detail page sections</h3>
          <p className="mt-1 text-xs leading-5 text-slate-500">
            Structured blocks for website product/service detail pages (hero, use cases, tables,
            feature cards, infographics). Websites read this from{" "}
            <code className="rounded bg-slate-100 px-1">sectionsJson</code> on the public detail API.
          </p>
        </div>
        <button
          type="button"
          className="shrink-0 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          onClick={() => setShowJson((v) => !v)}
        >
          {showJson ? "Hide JSON" : "Advanced JSON"}
        </button>
      </div>

      {showJson ? (
        <div>
          <textarea
            className={`${blogInputClass} font-mono text-xs`}
            rows={14}
            value={form.sectionsJson || ""}
            onChange={(e) => {
              const value = e.target.value;
              setForm((f) => ({ ...f, sectionsJson: value }));
              if (!value.trim()) {
                setJsonError("");
                return;
              }
              try {
                JSON.parse(value);
                setJsonError("");
              } catch {
                setJsonError("Invalid JSON");
              }
            }}
            placeholder='{"version":1,"sections":[]}'
          />
          {jsonError ? <p className="mt-1 text-xs text-red-600">{jsonError}</p> : null}
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <select
              className={blogInputClass}
              value={addType}
              onChange={(e) => setAddType(e.target.value)}
            >
              {SECTION_TYPES.map((item) => (
                <option key={item.type} value={item.type}>
                  {item.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="rounded-lg bg-brand-800 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
              onClick={() =>
                commit({
                  ...doc,
                  sections: [...sections, createEmptySection(addType)]
                })
              }
            >
              Add section
            </button>
          </div>

          {sections.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
              No detail sections yet. Add a Hero section to start matching the product detail layout.
            </div>
          ) : (
            <div className="space-y-4">
              {sections.map((section, index) => {
                const typeLabel =
                  SECTION_TYPES.find((t) => t.type === section.type)?.label || section.type;
                return (
                  <div key={section.id || `section-${index}`} className={cardClass}>
                    <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200">
                          {index + 1}. {typeLabel}
                        </span>
                        <label className="flex items-center gap-1.5 text-xs text-slate-600">
                          <input
                            type="checkbox"
                            checked={section.enabled !== false}
                            onChange={(e) =>
                              updateSection(index, { ...section, enabled: e.target.checked })
                            }
                          />
                          Enabled on website
                        </label>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        <button
                          type="button"
                          className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600 disabled:opacity-40"
                          disabled={index === 0}
                          onClick={() => moveSection(index, -1)}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600 disabled:opacity-40"
                          disabled={index === sections.length - 1}
                          onClick={() => moveSection(index, 1)}
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          className="rounded-md border border-red-200 bg-white px-2 py-1 text-xs text-red-600"
                          onClick={() =>
                            commit({
                              ...doc,
                              sections: sections.filter((_, i) => i !== index)
                            })
                          }
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                    <SectionFields
                      section={section}
                      onChange={(next) => updateSection(index, next)}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
